import React, { useEffect, useState } from "react";
import { Smartphone, Download, X } from "lucide-react";

/**
 * InstallPwaBanner (iter183) — slim in-page variant of InstallPwaCard,
 * meant for high-traffic feed pages like /browse where a full 12-py
 * dark banner would break the map/filter layout.
 *
 * Same guard rails as the big card:
 *   • Silent when already installed (display-mode standalone)
 *   • Silent for 14 days after dismissal
 *   • Never shows on iOS unless the browser signals interest — we
 *     surface a mini "share → add to home screen" guide inline instead
 *
 * Chrome / Edge / Samsung Internet will fire `beforeinstallprompt`; we
 * defer it and trigger on the CTA. Any other browser gets the iOS-style
 * inline hint.
 */
const DISMISSED_KEY = "ccu:install-banner:dismissed";
const COOLDOWN_MS = 14 * 24 * 60 * 60 * 1000;

const isStandalone = () =>
  window.matchMedia?.("(display-mode: standalone)").matches ||
  window.navigator?.standalone === true;

const isIOS = () => /iP(hone|ad|od)/.test(navigator.userAgent);

export default function InstallPwaBanner() {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(true);   // hide until we know we're allowed
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return undefined;
    try {
      const raw = localStorage.getItem(DISMISSED_KEY);
      if (raw && Date.now() - parseInt(raw, 10) < COOLDOWN_MS) return undefined;
    } catch { /* fail open */ }
    setDismissed(false);
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    return () => window.removeEventListener("beforeinstallprompt", onPrompt);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISSED_KEY, String(Date.now())); } catch { /* ignore */ }
    setDismissed(true);
  };

  const onInstall = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch { /* user cancelled */ }
      setDeferred(null);
      dismiss();
      return;
    }
    // iOS / unsupported: show a one-line hint
    setShowIosHint(true);
  };

  if (dismissed) return null;

  return (
    <div className="mb-3 border hairline bg-gradient-to-r from-[#0e0e0e] to-[#2a1a10] text-white p-3 flex items-center gap-3" data-testid="install-pwa-banner">
      <div className="w-9 h-9 shrink-0 rounded-lg bg-[#ff4500]/20 border border-[#ff4500]/40 flex items-center justify-center">
        <Smartphone className="w-4 h-4 text-[#ff9260]" strokeWidth={1.8} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="mono text-[9px] uppercase tracking-widest text-[#ff9260] leading-tight">
          Add to home screen · no app store
        </div>
        <div className="text-sm font-bold leading-tight mt-0.5">
          Pop CampCompass on your phone — one tap, works offline.
        </div>
        {showIosHint && (
          <div className="text-[10px] mono text-white/75 mt-1 leading-tight" data-testid="install-pwa-banner-ios-hint">
            {isIOS()
              ? "iPhone: tap the Share icon ⤴ then \u201cAdd to Home Screen\u201d."
              : "Not supported in this browser — try Chrome or Edge on Android."}
          </div>
        )}
      </div>
      <button
        onClick={onInstall}
        className="shrink-0 bg-[#ff4500] hover:bg-[#e63d00] text-white text-[11px] font-black uppercase tracking-widest px-3 py-2 inline-flex items-center gap-1.5 shadow-[0_3px_0_0_#000] hover:translate-y-[1px] hover:shadow-[0_2px_0_0_#000] transition-transform"
        data-testid="install-pwa-banner-install"
      >
        <Download className="w-3 h-3" /> Install
      </button>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        className="shrink-0 text-white/60 hover:text-white p-1.5"
        data-testid="install-pwa-banner-dismiss"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
